import { useContext } from "react";
import { useParams } from "react-router-dom";

import { UserContext } from "@/app/providers";
import { SATOSPortal } from "@/data-adapter/SATOSPortal";
import { ChecklistStepLog } from "@/features/procedures/model/checklist/ChecklistStep";
import { ProcedureDetails } from "@/features/procedures/model/procedure/ProcedureDetail";
import { ProcedureDetailNode } from "@/features/procedures/model/procedure/ProcedureDetailNode";
import { ProcedureGroup } from "@/features/procedures/model/procedure/ProcedureGroup";
import { UserProcedureKnowledge } from "@/features/competency/model/competence/UserProcedureKnowledge";
import { ActiveProcedure } from "@/features/procedures/model/types/procedure";
import { Procedure } from "@/features/procedures/model/types/procedure";
import { ProcedureAccordionMoveDirection } from "@/features/procedures/model/types/procedureAccordionMoveDirection";
import { useQuery } from "@tanstack/react-query";

import { PagedResponse } from "@/model/PagedResponse";

import { RouteParams } from "@/service/Utilities";

export class ProcedureQueryKeys {
	static readonly procedureKnowledge = "procedureKnowledge";
	static readonly active = "active";
	static readonly checklist = "checklist";
	static readonly data = "data";
	static readonly dependencies = "dependencies";
	static readonly details = "details";
	static readonly document = "document";
	static readonly edit = "edit";
	static readonly metaData = "metaData";
	static readonly filterFolderEqCurrentOrderByNameAsc =
		"?$filter=Folder eq 'Current'&$orderby=Name asc";
	static readonly getupdatedchecklist = "getupdatedchecklist";
	static readonly groupFolders = "groupfolders";
	static readonly groupdetails = "groupdetails";
	static readonly log = "log";
	static readonly materialHandlingChecklistForEdit = "materialhandlingforedit";
	static readonly measurements = "measurements";
	static readonly node = "node";
	static readonly nodediagram = "nodediagram";
	static readonly nodediagramtemplate = "nodediagramtemplate";
	static readonly nodediagramtemplatelist = "nodediagramtemplatelist";
	static readonly orderByNameAsc = "?$orderby=Name asc";
	static readonly procedure = "procedure";
	static readonly procedureDetails = "procedureDetails";
	static readonly procedureGroups = "proceduregroups";
	static readonly procedureGroup = "procedureGroup";
	static readonly procedureNode = "procedureNode";
	static readonly procedureMetaData = "procedureMetaData";
	static readonly procedures = "procedures";
	static readonly search = "search";
	static readonly shift = "shift";
	static readonly status = "status";
	static readonly step = "step";
	static readonly updatechecklist = "updatechecklist";
	static readonly fileMerge = "fileMerge";
	static readonly importPlanFromWizard = "importPlanFromWizard";
	static readonly NodeDiagramTemplate = "NodeDiagramTemplate";
}

export const useFetchProcedures = (filter: string) => {
	return useQuery<Procedure[]>({
		queryKey: [ProcedureQueryKeys.procedures, filter],
		queryFn: () => SATOSPortal.GetProcedures(`list/${filter}`),
	});
};

export const useFetchActiveProcedures = (
	page: number,
	pageSize: number,
	search?: string,
	sort?: "ASC" | "DESC",
) => {
	return useQuery<PagedResponse<ActiveProcedure>>({
		queryKey: [
			ProcedureQueryKeys.procedures,
			ProcedureQueryKeys.active,
			{ page, pageSize, search, sort },
		],
		queryFn: () =>
			SATOSPortal.GetActiveProcedures(page, pageSize, search, sort),
		refetchInterval: 10000,
		placeholderData: (previousData) => previousData,
	});
};

export const useFetchProcedureGroups = (filter: string) => {
	const user = useContext(UserContext);
	const companyId = user?.CompanyId;

	return useQuery<ProcedureGroup[]>({
		queryKey: [ProcedureQueryKeys.procedureGroups, companyId, filter],
		queryFn: () => SATOSPortal.GetProcedureGroups(filter),
	});
};

export const useFetchProcedureKnowledgeStatus = () => {
	const { id } = useParams<RouteParams>();
	const procedureId = parseInt(id);

	return useQuery<UserProcedureKnowledge>({
		queryKey: [ProcedureQueryKeys.procedureKnowledge, procedureId],
		queryFn: () => SATOSPortal.GetProcedureKnowledgeStatus(procedureId),
	});
};

export { useFetchActiveProcedureDetails } from "@/features/procedures/api/procedure.queries";

export {
	useFetchProcedureNode,
	useFetchReviewChecklist,
} from "@/features/procedures/api/procedure.queries";

export {
	useFetchActiveProcedure,
	useFetchProcedureShiftConfiguration,
} from "@/features/procedures/api/procedure.queries";

export const useFetchProcedureStepLog = (
	showStepLog: boolean,
	checklistId: number | undefined,
	stepId: number,
) => {
	const { id } = useParams<RouteParams>();

	return useQuery<ChecklistStepLog>({
		queryKey: [
			ProcedureQueryKeys.procedureDetails,
			ProcedureQueryKeys.checklist,
			ProcedureQueryKeys.step,
			ProcedureQueryKeys.log,
			id,
			checklistId,
			stepId,
		],
		queryFn: () => SATOSPortal.GetProcedureStepLog(id, checklistId, stepId),
		enabled: !!(showStepLog && checklistId && stepId),
	});
};

export const useFetchSuggestionsByChecklistId = (
	checklistId: number | undefined,
) => {
	return useQuery<unknown>({
		queryKey: [
			ProcedureQueryKeys.procedureDetails,
			ProcedureQueryKeys.checklist,
			ProcedureQueryKeys.step,
			ProcedureQueryKeys.log,
			checklistId,
		],
		queryFn: () => SATOSPortal.GetProcedureSuggestionByChecklistId(checklistId),
		enabled: !!checklistId,
	});
};

export {
	useHandleProcedureDetails,
	useFetchActiveProcedureGroupFolders,
	sortGroupFolders,
} from "@/features/procedures/api/procedure.queries";

export const ReorderAccordion = (
	procedureDetails: ProcedureDetails | undefined,
	nodeToMove: ProcedureDetailNode,
	Direction: ProcedureAccordionMoveDirection,
) => {
	if (!procedureDetails) {
		return procedureDetails;
	}
	let TargetTreId: number | undefined = 0;
	const node = { ...nodeToMove };
	let OtherNode: ProcedureDetailNode | undefined;

	if (node.TreeParentId === 0) {
		if (Direction === "Up")
			if (node.TreeId > 1) {
				OtherNode = procedureDetails.ProcedureDetailNodes.find(
					(x) => x.TreeId === node.TreeId - 1,
				);
				if (OtherNode?.TreeId) {
					TargetTreId = OtherNode?.TreeId;
				}
			}

		if (Direction === "Down") {
			OtherNode = procedureDetails.ProcedureDetailNodes.find(
				(x) => x.TreeId === node.TreeId + 1,
			);
			if (OtherNode) TargetTreId = OtherNode.TreeId;
		}

		if (TargetTreId > 0 && OtherNode) {
			OtherNode.TreeId = node.TreeId;
			node.TreeId = TargetTreId;
			const OtherNodeBeforeId = OtherNode.NodeBeforeId;
			OtherNode.NodeBeforeId = node.NodeBeforeId;
			node.NodeBeforeId = OtherNodeBeforeId;
			const OtherNodeAfterId = OtherNode.NodeAfterId;
			OtherNode.NodeAfterId = node.NodeAfterId;
			node.NodeAfterId = OtherNodeAfterId;
			procedureDetails.ProcedureDetailNodes.sort(function (a, b) {
				return a.TreeId - b.TreeId;
			});
		}
	} else {
		const RootNode = procedureDetails.ProcedureDetailNodes.find(
			(x) => x.TreeNodeNumber === node.TreeParentId,
		);

		if (Direction === "Up")
			if (node?.NodeBeforeId && node.NodeBeforeId > 0) {
				OtherNode = RootNode?.Children.find(
					(x) => x.TreeId === node.NodeBeforeId,
				);
				TargetTreId = OtherNode?.TreeId;
			} else if (RootNode?.NodeBeforeId && RootNode.NodeBeforeId > 0) {
				OtherNode = RootNode.Children.find(
					(x) => x.TreeId === node.NodeBeforeId,
				);
				if (OtherNode) {
					OtherNode.NodeAfterId = undefined;
				}
				RootNode.Children.shift();

				const OtherRootNode = procedureDetails.ProcedureDetailNodes.find(
					(x) => x.TreeId === RootNode.NodeBeforeId,
				);

				if (OtherRootNode) {
					OtherRootNode.Children.push(node);
					OtherNode = OtherRootNode.Children[OtherRootNode.Children.length];
					OtherNode.NodeAfterId = OtherNode.TreeId + 1;
					node.NodeBeforeId = OtherNode.TreeId;
					node.TreeId = OtherNode.NodeAfterId;
					node.NodeAfterId = undefined;
				}
			}

		if (Direction === "Down")
			if (node.NodeAfterId && node.NodeAfterId > 0) {
				OtherNode = RootNode?.Children.find(
					(x) => x.TreeId === node.NodeAfterId,
				);
				TargetTreId = OtherNode?.TreeId;
			} else if (RootNode?.NodeAfterId && RootNode.NodeAfterId > 0) {
				const OtherRootNode = procedureDetails.ProcedureDetailNodes.find(
					(x) => x.SchemeId === RootNode.NodeAfterId,
				);
				RootNode.Children.pop();
				OtherNode = RootNode.Children.find(
					(x) => x.TreeId === node.NodeBeforeId,
				);

				if (OtherNode) {
					OtherNode.NodeAfterId = undefined;
				}
				let x = 1;
				OtherRootNode?.Children.unshift(node);
				OtherRootNode?.Children.map((child) => {
					if (x === 1) child.NodeBeforeId = undefined;
					else child.NodeBeforeId = x - 1;
					child.TreeId = x;
					if (child.NodeAfterId != null) child.NodeAfterId = x + 1;
					x++;
				});
			}

		if (TargetTreId && TargetTreId > 0) {
			if (OtherNode) {
				OtherNode.TreeId = node.TreeId;
				node.TreeId = TargetTreId;
				const OtherNodeBeforeId = OtherNode.NodeBeforeId;
				OtherNode.NodeBeforeId = node.NodeBeforeId;
				node.NodeBeforeId = OtherNodeBeforeId;
				const OtherNodeAfterId = OtherNode.NodeAfterId;
				OtherNode.NodeAfterId = node.NodeAfterId;
				node.NodeAfterId = OtherNodeAfterId;
			}
			procedureDetails.ProcedureDetailNodes.sort(function (a, b) {
				return a.TreeId - b.TreeId;
			});
		}
	}
	return procedureDetails;
};

export const useProcedureGroupFolders = (companyId: number | undefined) => {
	return useQuery({
		queryKey: [
			ProcedureQueryKeys.procedure,
			ProcedureQueryKeys.groupFolders,
			companyId,
		],
		queryFn: () => SATOSPortal.GetProcedureMetaDataFolders(companyId),
		enabled: !!companyId,
	});
};

export const GetDocumentsUsed = (companyId: number | undefined) => {
	return useQuery({
		queryKey: [ProcedureQueryKeys.document, companyId],
		queryFn: () => SATOSPortal.GetDocumentsUsed(),
		enabled: !!companyId,
	});
};

export { useFetchDocumentForCurrentNode } from "@/features/procedures/api/procedure.queries";

export { useGetUpdatedProcedures } from "@/features/procedures/api/procedure.queries";

export const useSearchForProcedureDocument = (search: string) => {
	return useQuery({
		queryKey: [
			ProcedureQueryKeys.procedure,
			ProcedureQueryKeys.document,
			ProcedureQueryKeys.search,
			search,
		],
		queryFn: () => SATOSPortal.GetProcedureDocuments(search),
	});
};

export {
	useFetchProcedureGroupDetails,
	useFetchProcedureMetaData,
} from "@/features/procedures/api/procedure.queries";
